import { supabase } from '../lib/supabase';

// Function to handle new user creation
export async function handleNewUserSignup(userId: string, metadata: any) {
  try {
    console.log('Handling new user signup:', { userId, metadata });

    // Get the Premium tier ID
    const { data: tierData, error: tierError } = await supabase
      .from('subscription_tiers')
      .select('id')
      .eq('name', 'Premium')
      .single();

    if (tierError) {
      console.error('Error getting Premium tier:', tierError);
      throw tierError;
    }

    // Update user's subscription
    const { error: updateError } = await supabase
      .from('user_subscriptions')
      .update({
        tier_id: tierData.id,
        active: true,
        expires_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString() // 14 days
      })
      .eq('user_id', userId);

    if (updateError) {
      console.error('Error updating subscription:', updateError);
      throw updateError;
    }

    console.log('User subscription updated successfully to Premium');

    return { success: true };
  } catch (error) {
    console.error('Error in handleNewUserSignup:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function handleNewUser(user: any, createExampleSermon: boolean = true) {
  try {
    // Create user profile
    const { error: profileError } = await supabase
      .from('profiles')
      .insert([
        {
          id: user.id,
          email: user.email,
          first_name: user.user_metadata?.first_name || '',
          last_name: user.user_metadata?.last_name || '',
          full_name: user.user_metadata?.full_name || `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim()
        }
      ]);

    if (profileError) {
      console.error('Error creating profile:', profileError);
      throw profileError;
    }

    const exampleSermonContent = `<p><strong>📝 This is an example sermon to let you preview the workflow and tools. You can delete it safely.</strong></p>

<p>Notice how you can type freely outside of sermon sections - this is great for personal notes, reminders, or additional content that doesn't fit into a specific section type.</p>

<div class="sermon-section sermon-section-scripture" data-type="scripture" data-section-id="scripture-1">
<h3>Scripture Reading</h3>
<p><strong>Romans 8:38-39</strong> - "For I am convinced that neither death nor life, neither angels nor demons, neither the present nor the future, nor any powers, neither height nor depth, nor anything else in all creation, will be able to separate us from the love of God that is in Christ Jesus our Lord."</p>
</div>

<p>You can add commentary, personal thoughts, or transition text between any sections. This flexibility allows you to structure your sermon exactly how you want it.</p>

<div class="sermon-section sermon-section-main-point" data-type="main-point" data-point-number="1" data-section-id="point-1">
<h3>Main Point 1: God's Love is Unconditional</h3>
<p>God's love for us doesn't depend on our performance or perfection. Even when we fall short, His love remains constant.</p>
<ul>
<li>Romans 8:38-39 reminds us that nothing can separate us from God's love</li>
<li>This love gives us security and peace</li>
<li>We can rest in knowing we are accepted and valued</li>
</ul>
</div>

<p>Here's some example text between main points. You might use this space for transitions, additional thoughts, or speaking notes that help connect your ideas.</p>

<div class="sermon-section sermon-section-main-point" data-type="main-point" data-point-number="2" data-section-id="point-2">
<h3>Main Point 2: Grace Empowers Transformation</h3>
<p>God's grace doesn't just forgive us; it empowers us to live differently. Through His grace, we can overcome our struggles and grow in faith.</p>
<ul>
<li>Grace is unmerited favor from God</li>
<li>It provides strength for daily challenges</li>
<li>Grace enables us to extend forgiveness to others</li>
</ul>
</div>

<p>Feel free to add illustrations, quotes, or personal stories in regular text like this. The sermon sections help organize your key points, but you have complete freedom to add content wherever needed.</p>

<div class="sermon-section sermon-section-illustration" data-type="illustration" data-section-id="illustration-1">
<h3>Illustration: The Lighthouse</h3>
<p>A lighthouse doesn't ring bells or fire cannons to call attention to itself. It just shines its light consistently, guiding ships to safety. Similarly, our faith should be a steady light that guides others to Christ through our consistent love and service.</p>
</div>

<div class="sermon-section sermon-section-application" data-type="application" data-section-id="application-1">
<h3>Application: Living Out Love</h3>
<p>This week, let's commit to:</p>
<ul>
<li>Showing kindness to someone who has hurt us</li>
<li>Offering help to a neighbor in need</li>
<li>Spending time in prayer, thanking God for His love</li>
</ul>
<p>Remember, small acts of love can have eternal impact.</p>
</div>

<p>You can end with personal notes, additional prayers, or any other content that helps you prepare for delivery. The editor gives you complete flexibility!</p>

<div class="sermon-section sermon-section-prayer" data-type="prayer" data-section-id="prayer-1">
<h3>Closing Prayer</h3>
<p>Lord, thank You for Your unfailing love and amazing grace. Help us to live as Your children, reflecting Your love to the world around us. May our lives be a testimony of Your goodness. In Christ's name, Amen.</p>
</div>`;

    if (createExampleSermon) {
      console.log('Creating example sermon for user:', user.id);

      const { data: newSermon, error: sermonError } = await supabase
        .from('sermons')
        .insert([
          {
            user_id: user.id,
            title: "Welcome: Understanding God's Love and Grace",
            scripture: "Romans 8:38-39, Ephesians 2:8-9",
            content: exampleSermonContent,
            status: 'complete',
            series_id: null,
            date: null,
            version: 1,
            summary: "An example sermon demonstrating the editor's features with sections on God's unconditional love and transforming grace.",
            archived: false
          }
        ])
        .select()
        .single();

      if (sermonError) {
        console.error('Error creating example sermon:', sermonError);
        console.error('Sermon error details:', {
          message: sermonError.message,
          details: sermonError.details,
          hint: sermonError.hint,
          code: sermonError.code
        });
        // Don't throw here - we don't want to fail user creation if sermon creation fails
      } else {
        console.log('Example sermon created successfully:', newSermon);
      }
    } else {
      console.log('Skipping example sermon creation');
    }

    console.log('User profile created successfully');
  } catch (error) {
    console.error('Error in handleNewUser:', error);
    throw error;
  }
}